import { Trans } from "@lingui/react/macro";
import { Box, Button, Link as MuiLink, Stack, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { Link, useLocation, useParams } from "react-router";
import { useAppConfig } from "@/contexts/AppConfigContext";

interface CookieBannerProps {
  onOnlyNecessary: () => void;
  onAcceptAll: () => void;
}

export default function CookieBanner({ onOnlyNecessary, onAcceptAll }: CookieBannerProps) {
  const params = useParams<{ language?: string; campground?: string }>();
  const location = useLocation();
  const { privacyPolicyUrl } = useAppConfig();

  const cookiePolicyLink = privacyPolicyUrl ? (
    <MuiLink
      href={privacyPolicyUrl}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ color: "inherit", textDecorationColor: "inherit" }}
    >
      <Trans>Privacy Policy</Trans>
    </MuiLink>
  ) : (
    <MuiLink
      component={Link}
      to={`/${params.language}/${params.campground}/privacy-policy`}
      state={{ returnTo: `${location.pathname}${location.search}${location.hash}` }}
      sx={{ color: "inherit", textDecorationColor: "inherit" }}
    >
      <Trans>Privacy Policy</Trans>
    </MuiLink>
  );

  return (
    <Box
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-description"
      data-cookie-banner-display="popup"
      sx={surfaceSx}
    >
      <Typography id="cookie-banner-title" variant="h4" sx={{ mb: 1.5 }}>
        <Trans>Cookies &amp; Privacy</Trans>
      </Typography>
      <Typography id="cookie-banner-description" variant="body2" sx={{ lineHeight: 1.45, mb: 2 }}>
        <Trans>
          We use necessary cookies to make this booking widget work. With your permission, we’d also
          like to use optional analytics cookies to help us understand and improve your booking
          experience. Learn more in our {cookiePolicyLink}.
        </Trans>
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <Button variant="contained" onClick={onOnlyNecessary} sx={actionButtonSx}>
          <Trans>Only necessary</Trans>
        </Button>
        <Button variant="contained" onClick={onAcceptAll} sx={actionButtonSx}>
          <Trans>Accept all</Trans>
        </Button>
      </Stack>
    </Box>
  );
}

const surfaceSx = {
  position: "fixed",
  left: { xs: "16px", sm: "24px" },
  right: { xs: "16px", sm: "24px" },
  bottom: { xs: "16px", sm: "24px" },
  zIndex: (theme: Theme) => theme.zIndex.modal - 1,
  maxWidth: 900,
  mx: "auto",
  p: { xs: 2, sm: 3 },
  borderRadius: 4,
  border: "1px solid rgba(255, 255, 255, 0.24)",
  backgroundColor: "rgba(82, 82, 82, 0.88)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  boxShadow: 10,
  color: "common.white",
  animation: "cookie-banner-enter 280ms cubic-bezier(0.2, 0.8, 0.2, 1) both",
  "@keyframes cookie-banner-enter": {
    from: { opacity: 0, transform: "translateY(32px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
  },
};

const actionButtonSx = {
  width: { xs: "100%", sm: 200 },
  minHeight: 58,
  py: 1,
  bgcolor: "common.white",
  color: "common.black",
  boxShadow: "none",
  fontSize: 16,
  "&:hover": {
    bgcolor: "grey.100",
    boxShadow: "none",
  },
};
